import { postMessageWithPromise } from "../../../../base/browser/postMessage";
import { ITracingService } from "../../../../platform/tracing/common/tracing.js";
import { IntervalTimer } from "../../../../base/common/async";
import { Emitter, Event } from "../../../../base/common/events";
import { Disposable, toDisposable, type IDisposable } from "../../../../base/common/lifecycle";
import type { StripEvent } from "../../../../base/common/eventStrip";
import { filterSensitiveResourceUrls, isInspectorOwnedMediaUrl, resourceDisplayName, resourceEntrySize, resourceType, summarizeResourceTransfers, type ResourceTransferEntry } from "../../../../base/browser/resourceTimings";
import { analyzeLongFrame, classifyLongFrame, type LongFrameLike } from "../../../../base/browser/longAnimationFrame";
import { ILogService } from "../../../../platform/log/common/log";
import { IPerformanceService, PerformanceChangedEvent, PerformanceSample, GpuMemorySavings, type ResourceTransferBreakdown } from "../common/performance";
import type { ProgramInfo } from "../../../../platform/webgl/common/renderStats";
import { IWebGLService } from "../../../../platform/webgl/common/webgl";
import type { RenderStatsSummary } from "../../../../platform/webgl/common/renderStats";
import type { SceneTriangleCounts } from "../../../../platform/webgl/common/sceneStats";
import { IResourceAnalysisService } from "../../../../platform/webgl/three.js/resourceAnalysisService";
import { IResourceService } from "../../../../platform/resources/common/resources";
import type { ResourceKind, ResourceKindCounts } from "../../../../platform/resources/common/resources";
import {
    estimateTargetFps,
    rateFps,
    rateFrameStability,
    worstRating,
    rateMemory,
    rateVram,
    rateTriangles,
    type DeviceProfile,
    type PerformanceRatings,
} from "../common/performanceRatings";




/** Every reported value, captured at pause so a frozen readout matches on-screen. */
interface FrozenSnapshot {
    fps: number | undefined;
    usedMemory: number | undefined;
    gpuMemory: number | undefined;
    gpuMemorySavings: GpuMemorySavings | undefined;
    renderStats: RenderStatsSummary | undefined;
    sceneTriangles: SceneTriangleCounts | undefined;
    resourceCounts: ReadonlyMap<ResourceKind, ResourceKindCounts> | undefined;
    totalResourceDownload: number | undefined;
    resourceTransferBreakdown: ResourceTransferBreakdown | undefined;
    targetFps: number;
    ratings: PerformanceRatings;
    history: readonly PerformanceSample[];
    events: readonly StripEvent[];
}

export class BrowserPerformanceService extends Disposable implements IPerformanceService {

    constructor(
        private readonly config: { isChromeExtension?: boolean },
        @ILogService private readonly logService: ILogService,
        @IWebGLService private readonly webgl: IWebGLService,
        @IResourceAnalysisService private readonly resourceAnalysis: IResourceAnalysisService,
        @IResourceService private readonly resources: IResourceService,
        @ITracingService private readonly tracing?: ITracingService,
    ) {
        super();

        this._deviceProfile = readDeviceProfile();
        this._register(this.observePerformanceEvents());

        const timer = this._register(new IntervalTimer());
        timer.cancelAndSet(async () => {
            let memoryChanged = false;
            let resourcesChanged = false;
            const usedMemory = this.usedMemory;
            if (usedMemory !== this._lastMemoryUsage) {
                this._lastMemoryUsage = usedMemory;
                memoryChanged = true;
            }

            const totalResourceDownload = await this.getTotalResourceDownload();
            if (totalResourceDownload !== undefined && totalResourceDownload !== this._totalResourceDownload) {
                this._totalResourceDownload = totalResourceDownload;
                resourcesChanged = true;
            }

            // Same window the fps counter uses, and `frames` IS that fps — so the
            // sums the render graph accumulated come out per displayed frame.
            const renderStats = this.sampleRenderStats();
            const sceneTriangles = this.sampleSceneTriangles();
            const renderChanged = !sameRenderStats(renderStats, this._renderStats)
                || !sameSceneTriangles(sceneTriangles, this._sceneTriangles);
            if (renderChanged) {
                this._renderStats = renderStats;
                this._sceneTriangles = sceneTriangles;
            }

            // rolling history for sparklines — one sample per second, so this fires
            // every tick (a fresh data point) even when the numbers are steady.
            const counts = this.resourceCounts;
            this._history.push({
                at: Date.now(),
                fps: this._fps ?? 0,
                memory: usedMemory ?? 0,
                gpuMemory: this.gpuMemory ?? 0,
                drawCalls: renderStats?.drawCalls ?? 0,
                triangles: renderStats?.triangles ?? 0,
                meshes: sceneTriangles?.meshes ?? 0,
                geometries: counts?.get("geometry")?.alive ?? 0,
                materials: counts?.get("material")?.alive ?? 0,
                textures: counts?.get("texture")?.alive ?? 0,
                programs: this.programInfo?.count ?? 0,
            });
            if (this._history.length > BrowserPerformanceService.HISTORY_SECONDS) this._history.shift();

            this._onDidChange.fire({
                memory: memoryChanged ? true : undefined,
                resources: resourcesChanged ? true : undefined,
                render: renderChanged ? true : undefined,
                history: true,
            });

        }, 1000);

        this.tick();
    }


    private tick = () => {
        if (this.isDisposed) return;

        const now = performance.now();
        const cutoff = now - 1000;

        let i = 0;
        while (i < this._times.length && this._times[i] <= cutoff) {
            i++;
        }
        if (i > 0) {
            this._times = this._times.slice(i);
        }
        this._times.push(now);
        this._fps = this._times.length;

        requestAnimationFrame(this.tick);
    };

    private _times: number[] = [];
    private _fps: number | undefined = undefined;
    get fps(): number | undefined {
        return this.frozenOr("fps", () => this._fps);
    }


    private _renderStats: RenderStatsSummary | undefined = undefined;
    get renderStats(): RenderStatsSummary | undefined {
        return this.frozenOr("renderStats", () => this._renderStats);
    }

    private static readonly HISTORY_SECONDS = 120;
    private _history: PerformanceSample[] = [];
    get history(): readonly PerformanceSample[] {
        return this.frozenOr("history", () => this._history);
    }

    // #region pause — freeze EVERYTHING the readout reports (the time-series AND the
    // instantaneous gauges) at the moment of pause, so a paused strip/copy AND the
    // AI-MCP report all see the exact numbers on screen. Recording keeps filling the
    // LIVE buffers underneath; resume just drops the snapshot.
    private _paused = false;
    private _frozen: FrozenSnapshot | null = null;
    get paused(): boolean { return this._paused; }
    setPaused(paused: boolean): void {
        if (paused === this._paused) return;
        if (paused) {
            this._frozen = this.captureSnapshot(); // captured while still LIVE, then flip
            this._paused = true;
        } else {
            this._paused = false;
            this._frozen = null;
        }
    }
    /** the frozen value when paused, else the live one. */
    private frozenOr<K extends keyof FrozenSnapshot>(key: K, live: () => FrozenSnapshot[K]): FrozenSnapshot[K] {
        return this._paused && this._frozen ? this._frozen[key] : live();
    }
    private captureSnapshot(): FrozenSnapshot {
        return {
            fps: this.fps,
            usedMemory: this.usedMemory,
            gpuMemory: this.gpuMemory,
            gpuMemorySavings: this.gpuMemorySavings,
            renderStats: this.renderStats,
            sceneTriangles: this.sceneTriangles,
            resourceCounts: this.resourceCounts ? new Map(this.resourceCounts) : undefined,
            totalResourceDownload: this.totalResourceDownload,
            resourceTransferBreakdown: this.resourceTransferBreakdown,
            targetFps: this.targetFps,
            ratings: this.ratings,
            history: [...this._history],
            events: [...this.events],
        };
    }
    // #endregion

    // #region performance events (jank + large resource loads) — passive PerformanceObservers
    private static readonly EVENTS_WINDOW_MS = 120_000;
    private static readonly EVENTS_MAX = 300;
    private static readonly JANK_MAJOR_MS = 80;         // a real stall (red); below this, only frames that actually blocked show grey
    private static readonly MINOR_JANK_COALESCE_MS = 250; // merge a burst of grey minors into one tick
    private static readonly RESOURCE_MIN_BYTES = 1024 * 1024; // 1 MB: skip the long tail of small files
    private static readonly INTERACTION_MIN_MS = 100;   // only SLOW interactions (input latency)
    private _events: StripEvent[] = [];
    /** interactionIds already recorded — one event per real interaction (a click is
     *  pointerdown+pointerup+click sharing an id; move/over/out have id 0). */
    private _seenInteractions = new Set<number>();
    get events(): readonly StripEvent[] {
        return this.frozenOr("events", () => {
            // prune to the window on read (cheap; events are few)
            const cutoff = Date.now() - BrowserPerformanceService.EVENTS_WINDOW_MS;
            if (this._events.length && this._events[0].at < cutoff) {
                this._events = this._events.filter((e) => e.at >= cutoff);
            }
            return this._events;
        });
    }
    private pushEvent(e: StripEvent): void {
        this._events.push(e);
        if (this._events.length > BrowserPerformanceService.EVENTS_MAX) this._events.shift();
    }
    /** True if a minor (grey) long frame was just recorded — coalesces a burst of
     *  minors into one tick so a stretch of slightly-slow frames doesn't smear grey. */
    private recentMinorJank(at: number): boolean {
        for (let i = this._events.length - 1; i >= 0; i--) {
            const e = this._events[i];
            if (at - e.at > BrowserPerformanceService.MINOR_JANK_COALESCE_MS) return false;
            if (e.kind === "jank" && e.severity === "minor") return true;
        }
        return false;
    }
    /** Passive observers — the callback fires only when an event happens, never on a
     *  timer; keep it trivial so observing jank never adds to the jank. */
    private observePerformanceEvents(): IDisposable {
        if (typeof PerformanceObserver === "undefined") return Disposable.None;
        const origin = typeof performance !== "undefined" ? (performance.timeOrigin ?? 0) : 0;
        const observationStart = performance.now();
        const observers: PerformanceObserver[] = [];

        try { // long-animation-frame is Chromium-only — observe() throws elsewhere
            const jank = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    const e = entry as unknown as LongFrameLike;
                    if (entry.startTime >= observationStart) this.tracing?.recordLongFrame(e);
                    // major = real stall (red); minor = long but only slightly blocking
                    // (grey); null = long-but-fine, skipped so the strip stays quiet.
                    const severity = classifyLongFrame(e, BrowserPerformanceService.JANK_MAJOR_MS);
                    if (!severity) continue;
                    const at = origin + entry.startTime;
                    if (severity === "minor" && this.recentMinorJank(at)) continue; // coalesce bursts
                    // LoAF carries per-script attribution AND a phase timeline — classify
                    // WHY the frame was long (script- vs rendering-bound) in one pass.
                    const a = analyzeLongFrame(e);
                    this.pushEvent({ at, kind: "jank", severity, label: "Long frame", magnitude: Math.round(entry.duration), detail: a.detail || undefined, url: a.url, charPosition: a.charPosition });
                }
            });
            jank.observe({ type: "long-animation-frame", buffered: true } as PerformanceObserverInit);
            observers.push(jank);
        } catch { /* LoAF unsupported */ }

        try {
            const res = new PerformanceObserver((list) => {
                for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
                    if (filterSensitiveResourceUrls(entry.name) === null) continue;
                    const size = resourceEntrySize(entry);
                    if (size < BrowserPerformanceService.RESOURCE_MIN_BYTES) continue;
                    this.pushEvent({ at: origin + (entry.responseEnd || entry.startTime), kind: "resource", label: resourceDisplayName(entry.name), magnitude: size, url: entry.name });
                }
            });
            res.observe({ type: "resource", buffered: true });
            observers.push(res);
        } catch { /* resource timing unsupported */ }

        try { // Event Timing API — SLOW interactions only (INP-style), no global listeners
            const input = new PerformanceObserver((list) => {
                for (const entry of list.getEntries() as (PerformanceEntry & { interactionId?: number })[]) {
                    const id = entry.interactionId ?? 0;
                    if (!id) continue;                              // move/over/out — not a user interaction
                    if (this._seenInteractions.has(id)) continue;   // one event per click/tap/keypress
                    this._seenInteractions.add(id);
                    if (this._seenInteractions.size > 2000) this._seenInteractions.clear();
                    this.pushEvent({ at: origin + entry.startTime, kind: "interaction", label: entry.name || "interaction", magnitude: Math.round(entry.duration) });
                }
            });
            input.observe({ type: "event", durationThreshold: BrowserPerformanceService.INTERACTION_MIN_MS, buffered: true } as PerformanceObserverInit);
            observers.push(input);
        } catch { /* event timing unsupported */ }

        return toDisposable(() => observers.forEach((o) => o.disconnect()));
    }
    // #endregion

    private _sceneTriangles: SceneTriangleCounts | undefined = undefined;
    get sceneTriangles(): SceneTriangleCounts | undefined {
        return this.frozenOr("sceneTriangles", () => this._sceneTriangles);
    }

    get resourceCounts(): ReadonlyMap<ResourceKind, ResourceKindCounts> | undefined {
        return this.frozenOr("resourceCounts", () => this.resources.counts());
    }

    get programInfo(): ProgramInfo | undefined {
        // engine-agnostic, same shape as the other stat reads: an engine that does
        // not implement it simply contributes nothing
        for (const engine of this.webgl.getAllEngines()) {
            const info = engine.getProgramInfo?.();
            if (info) return info;
        }
        return undefined;
    }

    get gpuMemory(): number | undefined {
        return this.frozenOr("gpuMemory", () => {
            const report = this.resourceAnalysis.getReport();
            if (!report) return undefined;
            // NOTHING TRACKED is not the same as nothing used: an empty registry
            // summed to a confident "0 B" while the page rasterised 1.5M
            // triangles. Say we do not know — and ask for a scan, so the answer
            // does not depend on the user opening the Resources panel.
            const tracked = this.trackedResourceCount();
            if (tracked === 0) {
                this.requestResourceScan();
                return undefined;
            }
            // Registry filled, analysis has not caught up yet (it runs on idle):
            // still not an answer we have earned.
            if (report.entriesMeasured === 0) return undefined;
            let vram = 0;
            for (const kind of Object.values(report.byKind)) vram += kind?.vram ?? 0;
            return vram;
        });
    }

    /** live entries across every kind — 0 means the tracker has seen nothing yet */
    private trackedResourceCount(): number {
        let alive = 0;
        for (const counts of this.resources.counts().values()) alive += counts?.alive ?? 0;
        return alive;
    }

    /** Once per session: a rescan is a full hierarchy walk, and a poll that keeps
     *  finding nothing must not keep paying for it. */
    private _requestedResourceScan = false;
    private requestResourceScan(): void {
        if (this._requestedResourceScan) return;
        this._requestedResourceScan = true;
        for (const engine of this.webgl.getAllEngines()) {
            (engine as { rescanResources?: () => void }).rescanResources?.();
        }
    }

    get gpuMemorySavings(): GpuMemorySavings | undefined {
        return this.frozenOr("gpuMemorySavings", () => {
            const report = this.resourceAnalysis.getReport();
            if (!report) return undefined;
            return {
                compress: report.totalCompressibleVram,
                duplicate: report.totalRedundantVram,
                textures: report.compressibleTextures,
                geometry: report.totalGeometryVram,
                geometries: report.compressibleGeometries,
                hdr: report.totalHdrVram,
            };
        });
    }

    private readonly _deviceProfile: DeviceProfile;
    get deviceProfile(): DeviceProfile {
        return this._deviceProfile;
    }

    private _targetProfile: DeviceProfile | null = null;
    get targetProfile(): DeviceProfile | null {
        return this._targetProfile;
    }
    setTargetProfile(profile: DeviceProfile | null): void {
        this._targetProfile = profile;
        // ratings re-derive against the new target — nudge the UI to re-read
        this._onDidChange.fire({ ratings: true });
    }

    /** target merged over auto (target budgets win; auto fills gaps), or just auto. */
    get effectiveProfile(): DeviceProfile {
        return this._targetProfile ? { ...this._deviceProfile, ...this._targetProfile } : this._deviceProfile;
    }

    /**
     * The DISPLAY CEILING: the observed peak fps snapped to a cadence. rAF is
     * vsync-locked, so the peak ≈ the display refresh — the most fps we could ever
     * show here. fps is rated against THIS, never a target device's fps: fps isn't
     * portable (it's this machine's GPU/display), and you can't be faulted for not
     * exceeding your own refresh. Dropping BELOW it is the portable signal ("already
     * struggling here → worse on the target").
     */
    get targetFps(): number {
        return this.frozenOr("targetFps", () => {
            let peak = this._fps ?? 0;
            for (const s of this._history) if (s.fps > peak) peak = s.fps;
            return estimateTargetFps(peak);
        });
    }

    get ratings(): PerformanceRatings {
        return this.frozenOr("ratings", () => {
            const profile = this.effectiveProfile;
            const ratings: PerformanceRatings = {};
            if (this._fps !== undefined) {
                const target = this.targetFps;
                // fold sustained frame drops over the window into the fps rating — a
                // smooth instant shouldn't read "good" if the last minute kept dipping.
                const stability = rateFrameStability(this._history.map((s) => s.fps), target);
                ratings.fps = worstRating(rateFps(this._fps, target), stability) ?? rateFps(this._fps, target);
            }
            const used = this.usedMemory;
            if (used !== undefined) {
                const m = rateMemory(used, profile);
                if (m) ratings.memory = m;
            }
            const gpu = this.gpuMemory;
            if (gpu !== undefined) {
                const g = rateVram(gpu, profile);
                if (g) ratings.gpuMemory = g;
            }
            const tris = this._renderStats?.triangles;
            if (tris !== undefined) {
                const t = rateTriangles(tris, profile);
                if (t) ratings.triangles = t;
            }
            return ratings;
        });
    }

    /** First engine that reports a scene (same engine-agnostic pattern as render stats). */
    get renderers(): readonly { type: string; active: boolean }[] {
        return this.webgl.describeRenderers?.() ?? [];
    }

    private sampleSceneTriangles(): SceneTriangleCounts | undefined {
        for (const engine of this.webgl.getAllEngines()) {
            const counts = engine.getSceneTriangleCounts?.();
            if (counts) return counts;
        }
        return undefined;
    }

    /**
     * The first engine that has something to report. Engine-agnostic on purpose:
     * `getRenderStats` is optional on IWebGLEngine, so an engine that does not
     * track passes simply contributes nothing rather than needing a special case.
     */
    private sampleRenderStats(): RenderStatsSummary | undefined {
        // the window matches the fps counter's, and `frames` IS that fps, so the
        // accumulated sums come out per displayed frame
        const opts = { windowMs: 1000, frames: this._fps ?? 60 };
        for (const engine of this.webgl.getAllEngines()) {
            const stats = engine.getRenderStats?.(opts);
            if (stats) return stats;
        }
        return undefined;
    }


    private readonly _onDidChange = new Emitter<PerformanceChangedEvent>();
    readonly onDidChange: Event<PerformanceChangedEvent> = this._onDidChange.event;

    private _lastMemoryUsage: number | undefined = undefined;
    get usedMemory(): number | undefined {
        return this.frozenOr("usedMemory", () => window.performance?.memory?.usedJSHeapSize);
    }

    get totalMemory(): number | undefined {
        if (window.performance?.memory) {
            return window.performance.memory.totalJSHeapSize;
        }
    }


    private _totalResourceDownload: number | undefined = undefined;

    get totalResourceDownload(): number | undefined {
        return this.frozenOr("totalResourceDownload", () => this._totalResourceDownload);
    }

    private _resourceTransferBreakdown: ResourceTransferBreakdown | undefined;
    get resourceTransferBreakdown(): ResourceTransferBreakdown | undefined {
        return this.frozenOr("resourceTransferBreakdown", () => this._resourceTransferBreakdown);
    }


    private _postMessageRequest: Promise<any> | null = null;
    private _loggedEntries: Set<string> = new Set();

    private async getTotalResourceDownload() {

        if (this.config.isChromeExtension) {
            try {
                if (this._postMessageRequest) {
                    return undefined;
                }
                this._postMessageRequest = postMessageWithPromise({
                    type: "needle:resources:getDownloadSize"
                }, undefined, { timeout: 2000 });
                const res = await this._postMessageRequest
                    .catch(e => {
                        this.logService.warn("Error getting resource download size from service worker:", e);
                        return undefined;
                    })
                    .finally(() => {
                        this._postMessageRequest = null;
                    })
                if (res && res.success && typeof res.totalBytes === "number") {
                    // if the response contains missing URLs it indicates we should collect them client side
                    // these urls did not have content-length info in the service worker (e.g. https://windland-neotix.vercel.app/)
                    // where the only way to get their size is via the Performance API
                    const missingEntries = this.updateMissingUrlsSizes(res.missingUrls)
                        .filter(entry => !isInspectorOwnedMediaUrl(entry.url));
                    const visibleMissingEntries = missingEntries.filter(entry => filterSensitiveResourceUrls(entry.url) !== null);
                    const workerFiles = (Array.isArray(res.largestResources) ? res.largestResources : [])
                        .filter((entry: ResourceTransferEntry) => filterSensitiveResourceUrls(entry.url) !== null);
                    const typeTotals = new Map<string, number>();
                    for (const entry of Array.isArray(res.resourceTypes) ? res.resourceTypes : []) {
                        if (typeof entry?.type === "string" && typeof entry?.bytes === "number") {
                            typeTotals.set(entry.type, (typeTotals.get(entry.type) ?? 0) + entry.bytes);
                        }
                    }
                    for (const entry of missingEntries) {
                        typeTotals.set(entry.type, (typeTotals.get(entry.type) ?? 0) + entry.bytes);
                    }
                    this._resourceTransferBreakdown = {
                        largestFiles: summarizeResourceTransfers([...workerFiles, ...visibleMissingEntries]).largestFiles,
                        largestTypes: [...typeTotals].map(([type, bytes]) => ({ type, bytes }))
                            .sort((a, b) => b.bytes - a.bytes).slice(0, 5),
                    };
                    // 0 = the worker attached AFTER the page loaded and saw
                    // no responses — unmeasured, not a 0-byte scene
                    const total = res.totalBytes + missingEntries.reduce((sum, entry) => sum + entry.bytes, 0);
                    return total > 0 ? total : undefined;
                }
                return undefined;
            }
            catch (e) {
                this._postMessageRequest = null;
                this.logService.error("Error getting resource download size from service worker:", e);
                return undefined;
            }
        }

        if (window.performance) {
            const resources = window.performance.getEntriesByType("resource");
            let total = 0;
            const entries: ResourceTransferEntry[] = [];
            const visibleEntries: ResourceTransferEntry[] = [];
            for (let i = 0; i < resources.length; i++) {
                const entry = resources[i] as PerformanceResourceTiming;
                if (isInspectorOwnedMediaUrl(entry.name)) continue;
                let size = 0;
                if (entry.transferSize !== undefined && entry.transferSize > 0) {
                    size = entry.transferSize;
                }
                else if (entry.encodedBodySize !== undefined && entry.encodedBodySize > 0) {
                    size = entry.encodedBodySize;
                }
                else {
                    if (!this._loggedEntries.has(entry.name)) {
                        this._loggedEntries.add(entry.name);
                        this.logService.warn(`Performance resource entry has no transferSize or encodedBodySize:`, entry.name, entry);
                    }
                }
                if (size > 0) {
                    total += size;
                    const transfer = { url: entry.name, bytes: size, type: resourceType(entry.name) };
                    entries.push(transfer);
                    if (filterSensitiveResourceUrls(entry.name) !== null) visibleEntries.push(transfer);
                }
            }
            this._resourceTransferBreakdown = {
                largestFiles: summarizeResourceTransfers(visibleEntries).largestFiles,
                largestTypes: summarizeResourceTransfers(entries).largestTypes,
            };
            // all-zero entries (cleared timing buffer, cross-origin without
            // Timing-Allow-Origin) = nothing actually measured
            return total > 0 ? total : undefined;
        }
        return undefined;
    }

    private readonly _missingUrlsSizes: Map<string, number> = new Map();

    private updateMissingUrlsSizes(newMissingUrls?: string[]): Required<ResourceTransferEntry>[] {
        if (newMissingUrls && Array.isArray(newMissingUrls) && newMissingUrls.length > 0) {
            this.logService.debug(`Performance missing URLs to check:`, newMissingUrls);
            const perf = window.performance;
            if (perf) {
                const resources = perf.getEntriesByType("resource");
                for (let i = 0; i < resources.length; i++) {
                    const entry = resources[i] as PerformanceResourceTiming;
                    if (newMissingUrls.includes(entry.name)) {
                        let size = 0;
                        if (entry.transferSize !== undefined && entry.transferSize > 0) {
                            size = entry.transferSize;
                        }
                        else if (entry.encodedBodySize !== undefined && entry.encodedBodySize > 0) {
                            size = entry.encodedBodySize;
                        }
                        if (size > 0) {
                            this.logService.debug(`Performance missing URL size found:`, entry.name, size, entry);
                            this._missingUrlsSizes.set(entry.name, size);
                        }
                    }
                }
            }
        }
        return [...this._missingUrlsSizes]
            .map(([url, bytes]) => ({ url, bytes, type: resourceType(url) }));
    }

}

/** Read whatever device signals the browser exposes (all optional / best-effort). */
function readDeviceProfile(): DeviceProfile {
    const profile: DeviceProfile = {};
    if (typeof navigator !== "undefined") {
        // deviceMemory / userAgentData are not in the standard lib types
        const nav = navigator as Navigator & { deviceMemory?: number; userAgentData?: { mobile?: boolean } };
        if (typeof nav.deviceMemory === "number") profile.deviceMemoryGB = nav.deviceMemory;
        if (typeof nav.hardwareConcurrency === "number") profile.cores = nav.hardwareConcurrency;
        if (typeof nav.userAgentData?.mobile === "boolean") profile.mobile = nav.userAgentData.mobile;
        else if (typeof window !== "undefined" && window.matchMedia)
            profile.mobile = window.matchMedia("(pointer: coarse)").matches;
    }
    const limit = typeof window !== "undefined" ? window.performance?.memory?.jsHeapSizeLimit : undefined;
    if (typeof limit === "number" && limit > 0) profile.heapLimitBytes = limit;
    // Browsers don't expose real VRAM. Estimate a soft budget from deviceMemory
    // (system RAM proxy; integrated GPUs share it): ~half, floored, so the GPU-memory
    // rating is a rough "getting heavy" signal — a PRO target profile can override it.
    if (profile.deviceMemoryGB !== undefined) {
        profile.vramBudgetBytes = Math.max(0.5, profile.deviceMemoryGB * 0.5) * 1024 * 1024 * 1024;
    }
    return profile;
}

/** Avoids firing a change (and a UI re-render) every second on a still scene. */
function sameRenderStats(a: RenderStatsSummary | undefined, b: RenderStatsSummary | undefined): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.drawCalls === b.drawCalls && a.triangles === b.triangles && a.passes === b.passes;
}

function sameSceneTriangles(a: SceneTriangleCounts | undefined, b: SceneTriangleCounts | undefined): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.visible === b.visible && a.total === b.total
        && a.meshes === b.meshes && a.materials === b.materials && a.geometries === b.geometries
        && a.nodes === b.nodes && a.maxDepth === b.maxDepth && a.averageDepth === b.averageDepth
        && a.p95Depth === b.p95Depth && a.nonRenderingNodes === b.nonRenderingNodes
        && a.singleChildNonRenderingNodes === b.singleChildNonRenderingNodes
        && a.longestSingleChildChain === b.longestSingleChildChain;
}
